
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Logger } from '@app/core/logger.service';
import { TermsComponent } from '@app/CustomControls/terms/terms.component';
import { UserBlockComponent } from '@app/CustomControls/user-block/user-block.component';
import { ITenantData } from '@app/Models/ITenantData';
import { environment } from '@env/environment';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Observable, of as observableOf } from 'rxjs';

const serviceKey: string = 'Authentication';
const log = new Logger(`IDMS: ${serviceKey}`);

export interface Context {
  idms: string;
  rbac: string;
}

const contextKey = 'context';
const codeKey = 'code';
const urlsKey = 'urls';
const tokenKey = 'token'

/* Provides a base for authentication workflow.*/
@Injectable()
export class AuthenticationService {
  private _context: Context | null;
  public ttdata: ITenantData = { users_count: 0, coolers_count: 0, critical_alarms_count: 0, noncritical_alarms_count: 0, license_valid_date: null };
  private readonly messageSource = new BehaviorSubject(true);
  private readonly tenantmessageSource = new BehaviorSubject(this.ttdata);
  currentMessage = this.messageSource.asObservable();
  tenantMessage = this.tenantmessageSource.asObservable();
  public users_count: number;
  public coolers_count: number;
  public critical_alarms_count: number;
  public noncritical_alarms_count: number;
  expiry_date: Date = null;
  isTermsAccepted: boolean;
  private readonly _userLoginIn$ = new BehaviorSubject(false);
  userLoginIn$ = this._userLoginIn$.asObservable();

  updateIsUserLoginIn(): void {
    this._userLoginIn$.next(this.isAuthenticated());
  }
  constructor(private readonly _http: HttpClient, private readonly router: Router, private readonly modalService: NgbModal) {
    const text: string | null = sessionStorage.getItem(contextKey) || localStorage.getItem(contextKey);
    this._context = text && JSON.parse(text);
  }
  changeMessage(message: boolean) {
    this.messageSource.next(message)
  }
  tenantChangeMessage(message1: ITenantData) {
    this.tenantmessageSource.next(message1);
  }

  /** Authenticates the user. stores context(user info, token info), idms urls and code which is provided after login */
  login(remember?: boolean) {

    const code: string = sessionStorage.getItem(codeKey);
    if (!sessionStorage.getItem(urlsKey) || !sessionStorage.getItem(codeKey)) {
      this.getIdmsUrls().subscribe(response => {
        sessionStorage.setItem(urlsKey, JSON.stringify(response));
        if (!sessionStorage.getItem(codeKey)) {
          //Redirect to login with default language a English always
          window.location.href = `${response.loginEndPoint}&lang=${environment.defaultLanguage}`;
        }
      });
    }

    if (sessionStorage.getItem(codeKey)) {
      this.getContext(code) //Get User and Token Detail
        .subscribe(response => {
          sessionStorage.setItem(tokenKey, response.toString().replace('"', ''));
          const context = { idms: this.ParseJwt(response.toString()), rbac: null }
          this.login_idms(context).subscribe(c => {
            if (c.idms) {
              this.addUserInfo() //adduserinfo api call
                //if 0 user blocked by admin - contact
                //non zero proceed!
                .subscribe(userInfoResponse => {
                  if (userInfoResponse === 0) {
                    this.changeMessage(false);
                    //check if token is there - TBD
                    this.OpenBlockedUserPopup();
                  }
                  else {
                    this.checkTermsAccept(context);
                  }
                });
            }
          });
        });
    }
  }

  public checkTermsAccept(context) {
    this.CheckTermsAccepted()
      .subscribe((termResponse) => {
        if (termResponse === true) { //true -proceed get rbac
          this.isTermsAccepted = true;
          this.UpdateRbacDetails(context);
        }
        else {
          this.isTermsAccepted = false;
          this.OpenTermsPopup() //false - terms popup - modal trigger handled in dash and admin dash
        }
      }, (error) => {
        console.error(error.error);
      });
  }
  public OpenTermsPopup() {
    this.modalService.open(TermsComponent, { backdrop: 'static', keyboard: false, size: 'lg' });
  }

  public OpenBlockedUserPopup() {
    this.modalService.open(UserBlockComponent, { backdrop: 'static', keyboard: false, size: 'lg' });
  }

  public UpdateRbacDetails(context) {
    context = context || this._context;
    this.getRBACDetails().subscribe(response => {
      if (response) {
        context.rbac = response;
        this.setContext(context);
        this.navigateToPage('');
      }
    }, (error) => {
      console.error(error.error);
    });
  }

  /** clears the session storage and logs out the user.*/
  logout() {
    const logout: string = JSON.parse(this.urls).logout;
    const id_token = this.ParseJwt(this.token).idToken
    let queryparams = `&id_token_hint=${id_token}&federated=true`;
    this.TerminateActiveSession().subscribe(res => {
    });
    this.setContext();

    window.location.href = logout + queryparams;
  }

  TerminateActiveSession() {
    const apiUrl = `${environment.serverUrl}/Authentication/TerminateActiveSession`;
    const httpOptions = {
      headers: new HttpHeaders({
        'accessToken': this.token,
        Authorization: `Bearer ${this.token}`
      })
    };
    return this._http.delete(apiUrl, httpOptions);
  }

  RefreshAuthToken() {
    const refreshToken = this.ParseJwt(this.token).refreshToken.toString();
    const apiUrl = `${environment.serverUrl}/Authentication/refreshToken/${refreshToken}/accesstoken`;
    const httpOptions = {
      headers: new HttpHeaders({
        'accessToken': this.token
      }), responseType: 'text' as 'json'
    };
    return this._http.get(apiUrl, httpOptions);
  }

  validateLogin(url: string): boolean {
    if (this.isAuthenticated()) {
      this.updateIsUserLoginIn()
      return true;
    }
    else if (!sessionStorage.getItem("code")) {
      const queryParam = decodeURIComponent(window.location.search.substring(1));
      const code: string = queryParam.split('&')[0].split('code=')[1];
      const isLogin: boolean = queryParam.split('&')[0].split('login=')[1] === "true";
      const autologin = window.location.pathname === "/autologin";
      if (code !== undefined) {
        sessionStorage.setItem(codeKey, code);
      }
      else if (url === "/home" && !autologin && !isLogin) {
        return true;
      } else {

      }
      this.navigateToPage('oauth');
    }
    else {
      this.login();
    }
    return false;
  }

  private CheckTermsAccepted() {
    const apiUrl = `${environment.serverUrl}/User/IsTermAccepted`;
    const httpOptions = {
      headers: new HttpHeaders({
        'ResponseType': 'text',
        Authorization: `Bearer ${this.token}`
      })
    };
    return this._http.get(apiUrl, httpOptions);
  }

  /**
   * Authenticates the user.
   * @param {Context} context The login parameters.
   * @return {Observable<Context>} The user credentials.
   */
  private login_idms(context: Context, remember?: boolean): Observable<Context> {
    this.setContext(context, remember);
    return observableOf(context);
  }

  /*Properties Exposed*/
  /**
   * Checks is the user is authenticated.
   * @return {boolean} True if the user is authenticated.
   */
  isAuthenticated(): boolean {
    return !!this.context;
  }

  /**
   * Gets the user context.
   * @return {Context} The user context(user info, token info) or null if the user is not authenticated.
   */
  get context(): Context | null {
    return this._context;
  }

  /** Gets the user logged in user name*/
  get authenticatedUser(): string {
    return JSON.parse(JSON.stringify(this._context)).idms.name;
  }

  /** Gets the user logged in user email*/
  get authenticatedUserEmail(): string {
    return JSON.parse(JSON.stringify(this._context)).idms.email;
  }

  get authenticatedUserId(): string {
    return JSON.parse(JSON.stringify(this._context)).idms.userId;
  }

  get authenticatedUserFId(): string {
    return JSON.parse(JSON.stringify(this._context)).idms.federatedId;
  }

  get authenticatedRoleCd(): string {
    return JSON.parse(JSON.stringify(this._context)).rbac.roleCode;
  }

  /** Gets the idms url list*/
  get urls(): any {
    return JSON.parse(JSON.stringify(sessionStorage.getItem(urlsKey)));
  }

  /**
   * Sets the user context.
   * The context may be persisted across sessions by setting the `remember` parameter to true.
   * Otherwise, the credentials are only persisted for the current session.
   * @param {Context=} context The user information.
   * @param {boolean=} remember True to remember context across sessions.
   */
  public setContext(context?: Context, remember?: boolean) {
    this._context = context || null;

    if (context) {
      const storage = remember ? localStorage : sessionStorage;
      storage.setItem(contextKey, JSON.stringify(context));
    } else {
      sessionStorage.removeItem(contextKey);
      sessionStorage.removeItem(codeKey);
      sessionStorage.removeItem(urlsKey);
      sessionStorage.removeItem(tokenKey);
      localStorage.removeItem(contextKey);
    }
  }

  /**
   * Gets the user information from Industry Ecostruxure API.
   * @param {code=} code returned from IDMS login page.
   * @return {Context=} context which contains (Logged in User information and logged in user token details).
   */

  private getContext(code: string) {
    const body = { "isMobile": false };
    return this._http.post(`${environment.serverUrl}/Authentication/${code}/accesstoken`, body, { responseType: "text" });
    //.pipe(map((res: Response) => res.toString()));
  }

  public ParseJwt(token: any): any {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace('-', '+').replace('_', '/');
    return JSON.parse(window.atob(base64));
  };

  public getRBACDetails(): any {
    const apiUrl = `${environment.serverUrl}/User/RBACUser`;
    const httpOptions = {
      headers: new HttpHeaders({
        'ResponseType': 'text',
        Authorization: `Bearer ${this.token}`
      })
    };
    return this._http.get(apiUrl, httpOptions);
  }

  /**
   * Gets the Urls from Industry Ecostruxure API.
   * @return {Urls=} urls.
   */

  private getIdmsUrls(): Observable<any> {
    const options = {
      headers: new HttpHeaders({ 'redirectURL': environment.redirect_uri })
    }

    const authApiUrl = `${environment.serverUrl}/Authentication/GetLoginUrls`;
    return this._http.get(authApiUrl, options);

  }

  //obsolete
  public getLicenseStatus(): Observable<any> {
    const headers: HttpHeaders = new HttpHeaders({ 'Authorization': `Bearer ${this.token}` });
    return this._http.get(`${environment.serverUrl}/License/LicenseStatusHardTermination/${this.authenticatedUserEmail}`, { headers: headers, responseType: "json" });
  }

  //to be called after validating generated token
  public addUserInfo(): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'ResponseType': 'text',
        Authorization: `Bearer ${this.token}`
      })
    };
    return this._http.post(`${environment.serverUrl}/User/AddUserInfo`, {}, httpOptions);
  }

  get token(): string {
    const token = sessionStorage.getItem("token").replace('"', '');
    if (token === "") {
      log.error("token not available... login again");
    }
    return token;
  }

  public navigateToPage(page: string) {
    this.router.navigate([`/${page}`]);
  }

  public getTenantCount() {
    const headers: HttpHeaders = new HttpHeaders({ 'Authorization': `Bearer ${this.token}` });
    this._http.get(`${environment.serverUrl}/User/${this.authenticatedUserId}`, { headers: headers, responseType: "json" }).subscribe((result: ITenantData) => {
      this.tenantChangeMessage(result);
    })
  }

}
